import React, { forwardRef } from "react";
import styled, { keyframes } from "styled-components";
import { Link } from "react-router-dom";
import {
  FaArrowRight,
  FaClock,
  FaStar,
  FaTrophy,
  FaCheckCircle,
  FaGraduationCap,
} from "react-icons/fa";

/* =====================
  PAGE WRAPPER
===================== */
const PageBg = styled.section`
  background: #ffffff;
  padding: 4rem 5%;
  font-family: 'Times New Roman', Times, serif;

  @media (max-width: 1366px) and (min-width: 769px) {
    padding: 2rem 5%;
  }

  @media (max-width: 768px) {
    padding: 2.5rem 5%;
  }
`;

const Container = styled.div`
  max-width: 1200px;
  margin: auto;

  @media (max-width: 1366px) and (min-width: 769px) {
    max-width: 950px;
  }
`;

/* =====================
  HEADER
===================== */
const Head = styled.div`
  text-align: center;
  margin-bottom: 3.5rem;

  @media (max-width: 1366px) and (min-width: 769px) {
    margin-bottom: 1.5rem;
  }

  @media (max-width: 768px) {
    margin-bottom: 2rem;
  }
`;

const SectionLabel = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  background: rgba(55, 48, 163, 0.05);
  border: 1px solid rgba(55, 48, 163, 0.1);
  padding: 6px 16px;
  border-radius: 4px;
  margin-bottom: 1.2rem;
  font-size: 0.78rem;
  font-weight: 700;
  color: #3730A3;
  letter-spacing: 1.5px;
  text-transform: uppercase;

  &::before {
    content: '';
    width: 6px;
    height: 6px;
    background: #3730A3;
    border-radius: 50%;
  }

  @media (max-width: 1366px) and (min-width: 769px) {
    font-size: 0.65rem;
    padding: 4px 10px;
    margin-bottom: 0.6rem;
  }
`;

const Title = styled.h2`
  font-family: 'Times New Roman', Times, serif;
  font-size: 2.6rem;
  font-weight: 800;
  color: #1a1a2e;
  line-height: 1.2;
  margin-bottom: 0.8rem;

  em {
    font-style: normal;
    color: #3730a3;
  }

  @media (max-width: 1366px) and (min-width: 769px) {
    font-size: 1.7rem;
    margin-bottom: 0.4rem;
  }

  @media (max-width: 768px) {
    font-size: 1.7rem;
  }
`;

const Subtitle = styled.p`
  font-family: 'Times New Roman', Times, serif;
  color: #4a4a6a;
  font-size: 1rem;
  max-width: 520px;
  margin: 0 auto;
  line-height: 1.7;

  @media (max-width: 1366px) and (min-width: 769px) {
    font-size: 0.8rem;
    max-width: 420px;
    line-height: 1.5;
  }
`;

/* =====================
  CARDS
===================== */
const CardsWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2rem;

  @media (max-width: 1366px) and (min-width: 769px) {
    gap: 1.2rem;
  }

  @media (max-width: 768px) {
    gap: 1.25rem;
  }
`;

const Card = styled.div`
  background: #fff;
  border-radius: 16px;
  overflow: hidden;
  display: grid;
  grid-template-columns: 1fr 1fr;
  border: 1px solid rgba(55, 48, 163, 0.08);
  box-shadow: 0 2px 12px rgba(55, 48, 163, 0.03);

  @media (max-width: 1366px) and (min-width: 769px) {
    border-radius: 10px;
  }

  @media (max-width: 820px) {
    grid-template-columns: 1fr;
  }
`;

/* =====================
  IMAGE SIDE
===================== */
const ImgCol = styled.div`
  position: relative;
  min-height: 460px;
  background: #f5f3ff;
  overflow: hidden;
  order: ${(p) => p.order || 1};

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    display: block;
    position: absolute;
    inset: 0;
    z-index: 1;
  }

  @media (max-width: 1366px) and (min-width: 769px) {
    min-height: 320px;
  }

  @media (max-width: 820px) {
    min-height: 200px;
    order: 1;
  }
`;

/* =====================
  CONTENT SIDE
===================== */
const Body = styled.div`
  padding: 3rem 2.8rem;
  display: flex;
  flex-direction: column;
  justify-content: center;
  gap: 1.2rem;
  order: ${(p) => p.order || 2};

  @media (max-width: 1366px) and (min-width: 769px) {
    padding: 1.6rem 1.6rem;
    gap: 0.6rem;
  }

  @media (max-width: 820px) {
    padding: 1.5rem 1.35rem;
    gap: 0.8rem;
    order: 2;
  }
`;

const Tags = styled.div`
  display: flex;
  gap: 0.6rem;
  flex-wrap: wrap;

  @media (max-width: 1366px) and (min-width: 769px) {
    gap: 0.4rem;
  }
`;

const Tag = styled.span`
  font-family: 'Times New Roman', Times, serif;
  font-size: 0.72rem;
  font-weight: 700;
  letter-spacing: 0.4px;
  padding: 0.35rem 1rem;
  border-radius: 4px;
  background: rgba(55, 48, 163, 0.06);
  color: #3730A3;
  border: 1px solid rgba(55, 48, 163, 0.1);

  @media (max-width: 1366px) and (min-width: 769px) {
    font-size: 0.6rem;
    padding: 0.2rem 0.65rem;
  }
`;

const Divider = styled.div`
  width: 44px;
  height: 3px;
  border-radius: 2px;
  background: #3730A3;

  @media (max-width: 1366px) and (min-width: 769px) {
    width: 30px;
    height: 2px;
  }
`;

const CardTitle = styled.h3`
  font-family: 'Times New Roman', Times, serif;
  font-size: 2rem;
  font-weight: 800;
  color: #1a1a2e;
  line-height: 1.25;
  margin: 0;

  @media (max-width: 1366px) and (min-width: 769px) {
    font-size: 1.35rem;
  }

  @media (max-width: 768px) {
    font-size: 1.5rem;
  }
`;

const Desc = styled.p`
  font-family: 'Times New Roman', Times, serif;
  font-size: 0.98rem;
  color: #4a4a6a;
  line-height: 1.8;
  margin: 0;

  @media (max-width: 1366px) and (min-width: 769px) {
    font-size: 0.78rem;
    line-height: 1.5;
  }
`;

const FeatureList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.65rem;

  @media (max-width: 1366px) and (min-width: 769px) {
    gap: 0.35rem;
  }
`;

const FeatItem = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  font-family: 'Times New Roman', Times, serif;
  font-size: 0.92rem;
  color: #4a4a6a;
  font-weight: 500;

  @media (max-width: 1366px) and (min-width: 769px) {
    font-size: 0.72rem;
    gap: 0.4rem;
  }
`;

const FeatDot = styled.div`
  width: 22px;
  height: 22px;
  border-radius: 50%;
  background: #f5f3ff;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;

  svg {
    color: #3730A3;
    font-size: 10px;
  }

  @media (max-width: 1366px) and (min-width: 769px) {
    width: 16px;
    height: 16px;

    svg { font-size: 7px; }
  }
`;

const MetaRow = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-family: 'Times New Roman', Times, serif;
  font-size: 0.85rem;
  color: #4a4a6a;
  font-weight: 600;

  svg {
    color: #3730A3;
    font-size: 15px;
  }

  @media (max-width: 1366px) and (min-width: 769px) {
    font-size: 0.7rem;

    svg { font-size: 12px; }
  }
`;

const CTABtn = styled(Link)`
  display: inline-flex;
  align-items: center;
  gap: 0.55rem;
  padding: 0.9rem 2.2rem;
  border-radius: 50px;
  font-family: 'Times New Roman', Times, serif;
  font-weight: 700;
  font-size: 0.92rem;
  color: #fff;
  text-decoration: none;
  background: #3730A3;
  border: none;
  cursor: pointer;
  transition: background 0.3s ease;

  svg {
    font-size: 14px;
  }

  &:hover {
    background: #0b0b77;
  }

  @media (max-width: 1366px) and (min-width: 769px) {
    padding: 0.6rem 1.4rem;
    font-size: 0.75rem;

    svg { font-size: 12px; }
  }
`;

/* =====================
  FOOTER
===================== */
const FooterWrap = styled.div`
  text-align: center;
  margin-top: 2.5rem;

  @media (max-width: 1366px) and (min-width: 769px) {
    margin-top: 1.5rem;
  }
`;

const ViewAllBtn = styled(Link)`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.9rem 2.8rem;
  border-radius: 50px;
  border: 2px solid #3730a3;
  color: #3730a3;
  font-family: 'Times New Roman', Times, serif;
  font-weight: 700;
  font-size: 0.95rem;
  text-decoration: none;
  background: transparent;
  cursor: pointer;
  transition: all 0.3s ease;

  svg {
    transition: transform 0.3s ease;
  }

  &:hover {
    background: #3730a3;
    color: #fff;

    svg {
      transform: translateX(5px);
    }
  }

  @media (max-width: 1366px) and (min-width: 769px) {
    padding: 0.6rem 1.8rem;
    font-size: 0.78rem;
  }
`;

/* =====================
  COMPONENT
===================== */
const CoursesSection = forwardRef((props, ref) => {
  return (
    <PageBg id="courses">
      <Container>
        <Head ref={ref}>
          <SectionLabel>Flagship Programs</SectionLabel>
          <Title>
            Our <em>Flagship Programs</em>
          </Title>
          <Subtitle>
            Industry-recognized certification programs that have transformed
            thousands of careers in healthcare coding and documentation.
          </Subtitle>
        </Head>

        <CardsWrapper>
          <Card>
            <ImgCol order={1}>
              <img
                src="/images/ccs-certification.png"
                alt="CCS Certification"
                onError={(e) => { e.target.style.display = "none"; }}
              />
            </ImgCol>

            <Body order={2}>
              <Tags>
                <Tag>⭐ Most Popular</Tag>
                <Tag>AHIMA Certified</Tag>
              </Tags>
              <Divider />
              <CardTitle>CCS Certification Training</CardTitle>
              <Desc>
                Master medical coding with comprehensive CCS, CCS-P, and CCA
                certification training. Learn ICD-10-CM, ICD-10-PCS, CPT, and
                HCPCS from industry experts with a proven 98% certification
                pass rate.
              </Desc>
              <FeatureList>
                {[
                  { icon: <FaCheckCircle />, text: "5000+ Students Trained Globally" },
                  { icon: <FaTrophy />, text: "98% Certification Pass Rate" },
                  { icon: <FaGraduationCap />, text: "AHIMA & AAPC Approved Curriculum" },
                  { icon: <FaStar />, text: "Expert-Led Training with LearnThruUs Methodology" },
                ].map((f, i) => (
                  <FeatItem key={i}>
                    <FeatDot>{f.icon}</FeatDot>
                    {f.text}
                  </FeatItem>
                ))}
              </FeatureList>
              <MetaRow>
                <FaClock /> 60 Hours Training
              </MetaRow>
              <div>
                <CTABtn to="/ccs">
                  Explore CCS Program <FaArrowRight />
                </CTABtn>
              </div>
            </Body>
          </Card>

          <Card>
            <Body order={1}>
              <Tags>
                <Tag>🔥 In Demand</Tag>
                <Tag>AHIMA Approved</Tag>
              </Tags>
              <Divider />
              <CardTitle>CDI Training Program</CardTitle>
              <Desc>
                Master Clinical Documentation Improvement with hands-on
                training from India's first AHIMA Approved CDI Trainer. Learn
                query writing, physician engagement, and how CDI impacts DRG
                and HCC assignment.
              </Desc>
              <FeatureList>
                {[
                  { icon: <FaCheckCircle />, text: "First CDI Trainer from India" },
                  { icon: <FaTrophy />, text: "AHIMA Approved CDI Trainer — Gaayatri Jayapal" },
                  { icon: <FaGraduationCap />, text: "CCDS & CDIP Exam Preparation" },
                  { icon: <FaStar />, text: "Practical Query Writing & Physician Engagement" },
                ].map((f, i) => (
                  <FeatItem key={i}>
                    <FeatDot>{f.icon}</FeatDot>
                    {f.text}
                  </FeatItem>
                ))}
              </FeatureList>
              <MetaRow>
                <FaClock /> 50 Hours Training
              </MetaRow>
              <div>
                <CTABtn to="/cdi">
                  Explore CDI Program <FaArrowRight />
                </CTABtn>
              </div>
            </Body>

            <ImgCol order={2}>
              <img
                src="/images/cdi-certification.png"
                alt="CDI Certification"     
                onError={(e) => { e.target.style.display = "none"; }}
              />
            </ImgCol>
          </Card>
        </CardsWrapper>

        <FooterWrap>
          <ViewAllBtn to="/all-courses">
            View All Courses <FaArrowRight />
          </ViewAllBtn>
        </FooterWrap>
      </Container>
    </PageBg>
  );
});

export default CoursesSection;